// src/Register.js
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const API_URL =
  process.env.NODE_ENV === "production"
    ? (process.env.REACT_APP_API_URL || "https://api.hellastay.com")
    : "http://localhost:8000";

export default function Register({ setUser }) {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false); // ✅ NEW: terms checkbox
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // ✅ минимум 8 символа, само букви/цифри, минимум 1 главна, минимум 1 цифра
  const passwordRegex = /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{8,}$/;

  const canSubmit = useMemo(() => {
    const e = email.trim();
    return e.length > 3 && passwordRegex.test(password) && agree && !loading;
  }, [email, password, agree, loading]);

  const register = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!agree) {
      setError(
        t?.("register.errors.mustAgree") ||
          "Трябва да се съгласиш с Общите условия и Политиката за поверителност."
      );
      return;
    }

    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password; // не trim-ваме парола

    if (!cleanEmail || !cleanPassword) {
      setError(t("register.errors.enterEmailPassword"));
      return;
    }

    if (!passwordRegex.test(cleanPassword)) {
      setError(t("register.errors.passwordRule"));
      return;
    }

    try {
      setLoading(true);
      setError("");

      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // cookie JWT
        body: JSON.stringify({
          email: cleanEmail,
          password: cleanPassword,
          // NOTE: по желание може да пращаме agree=true към backend,
          // но засега го държим само фронт-валидиране.
        }),
      });

      // ✅ safe parse
      let data = {};
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        data = await res.json();
      } else {
        const text = await res.text();
        data = { message: text };
      }

      if (!res.ok) {
        setError(data.message || t("register.errors.registrationFailed"));
        return;
      }

      if (!data.user) {
        setError(t("register.errors.missingUserPayload"));
        return;
      }

      setUser(data.user);
      navigate("/");
    } catch (err) {
      setError(t("register.errors.cannotConnect"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-box">
        <h2>{t("register.title")}</h2>

        <div
          style={{
            color: "var(--muted)",
            fontWeight: 500,
            fontSize: 13,
            marginTop: -6,
          }}
        >
          {t("register.subtitle")}
        </div>

        {error && (
          <div style={{ color: "red", fontSize: 13, marginTop: 8 }}>
            {error}
          </div>
        )}

        <form onSubmit={register} style={{ marginTop: 10 }}>
          <input
            type="email"
            placeholder={t("register.emailPlaceholder")}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError("");
            }}
            autoComplete="email"
            inputMode="email"
          />

          <input
            type="password"
            placeholder={t("register.passwordPlaceholder")}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError("");
            }}
            autoComplete="new-password"
          />

          {/* ✅ NEW: Terms agreement checkbox */}
          <label
            style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              marginTop: 10,
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(34,34,34,0.10)",
              background: "rgba(255,255,255,0.85)",
              color: "var(--text)",
            }}
          >
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => {
                setAgree(e.target.checked);
                if (error) setError("");
              }}
              style={{
                marginTop: 3,
                width: 16,
                height: 16,
                accentColor: "var(--brand)",
                flex: "0 0 auto",
              }}
            />

            <span style={{ fontSize: 13, lineHeight: 1.35, color: "var(--muted)" }}>
              {t?.("register.agreePrefix") || "Съгласявам се с"}{" "}
              <Link
                to="/terms"
                style={{ color: "var(--brandDark)", fontWeight: 500 }}
                onClick={(e) => e.stopPropagation()}
              >
                {t?.("register.termsLink") || "Общите условия"}
              </Link>{" "}
              {t?.("register.and") || "и"}{" "}
              <Link
                to="/privacy"
                style={{ color: "var(--brandDark)", fontWeight: 500 }}
                onClick={(e) => e.stopPropagation()}
              >
                {t?.("register.privacyLink") || "Политиката за поверителност"}
              </Link>
              .
            </span>
          </label>

          <button
            type="submit"
            disabled={!canSubmit}
            style={{ opacity: canSubmit ? 1 : 0.6 }}
            title={
              !agree
                ? (t?.("register.mustAgreeTitle") ||
                   "Маркирай отметката, за да продължиш.")
                : canSubmit
                ? t("register.readyTitle")
                : t("register.passwordRuleTitle")
            }
          >
            {loading ? t("register.registering") : t("register.title")}
          </button>
        </form>

        <div className="switch">
          {t("register.haveAccount")}{" "}
          <Link to="/login">{t("register.loginLink")}</Link>
        </div>
      </div>
    </div>
  );
}